import { useCallback, useState } from 'react';
import {
  AppAccess,
  AppAccessType,
  DashboardSelectorSubmoduleItem,
} from '../types/dashboardSelector';
import { FilterGroupSelectionItem } from '../components/AppAccessSidepanel/FilterGroupsList';
import { SubmoduleFilterRegistry } from './useSubmoduleFilterRegistry';

type UseCustomAppsSidepanelParams = {
  onUpdateAppAccess: (submodule: DashboardSelectorSubmoduleItem, appAccess: AppAccess) => void;
  filterRegistry?: SubmoduleFilterRegistry;
  onRefreshSubmoduleFilters?: (submoduleId: string) => Promise<void>;
};

export const useCustomAppsSidepanel = ({
  onUpdateAppAccess,
  filterRegistry,
  onRefreshSubmoduleFilters,
}: UseCustomAppsSidepanelParams) => {
  const [activeSubmodule, setActiveSubmodule] = useState<DashboardSelectorSubmoduleItem | null>(
    null,
  );

  const handleSidepanelOpen = useCallback((submodule: DashboardSelectorSubmoduleItem) => {
    setActiveSubmodule(submodule);
  }, []);

  const handleSidepanelClose = useCallback(() => {
    setActiveSubmodule(null);
  }, []);

  const handleSidepanelSave = useCallback(
    (selection: FilterGroupSelectionItem[]) => {
      if (!activeSubmodule) return;
      const filters = selection.flatMap((filter) =>
        filter.options.map((option) => ({
          rowId: option.id,
          submoduleFilterId: option.submoduleFilterId,
          filterValue: option.filterValue,
        })),
      );
      onUpdateAppAccess(activeSubmodule, { type: AppAccessType.CUSTOM, filters });
    },
    [activeSubmodule, onUpdateAppAccess],
  );

  const filterMap =
    activeSubmodule && filterRegistry
      ? (filterRegistry.get(activeSubmodule.submoduleId) ?? null)
      : null;

  const handleRefresh =
    activeSubmodule && onRefreshSubmoduleFilters
      ? () => onRefreshSubmoduleFilters(activeSubmodule.submoduleId)
      : undefined;

  return {
    isOpen: !!activeSubmodule,
    activeSubmodule,
    filterMap,
    handleSidepanelOpen,
    handleSidepanelClose,
    handleSidepanelSave,
    handleRefresh,
  };
};
